import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronDown } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import {
  DASHBOARD_ITEM, PROCUREMENT_MODULES, CONTRACTS_MODULES, CLAIMS_LD_MODULES
} from '@/config/navigation';

const BASE_SECTIONS = [
  { title: 'Procurement Management', modules: [DASHBOARD_ITEM, ...PROCUREMENT_MODULES] },
  { title: 'Contracts Management', modules: CONTRACTS_MODULES },
  { title: 'Claims & LD Management', modules: CLAIMS_LD_MODULES },
];

export function AppLauncher({ open, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // iter-238 — filter sections + modules by user.restrictions.allowed_module_keys
  const SECTIONS = useMemo(() => {
    const allowed = user?.restrictions?.allowed_module_keys;
    if (!Array.isArray(allowed) || allowed.length === 0) return BASE_SECTIONS;
    return BASE_SECTIONS
      .map(s => ({ ...s, modules: s.modules.filter(m => allowed.includes(m.key) || m.key === 'dashboard') }))
      .filter(s => s.modules.length > 0);
  }, [user]);

  const activeModule = useMemo(() => {
    const all = [...PROCUREMENT_MODULES, ...CONTRACTS_MODULES, ...CLAIMS_LD_MODULES];
    for (const mod of all) {
      if (mod.children?.some(c => location.pathname === c.path || location.pathname.startsWith(c.path + '/'))) return mod;
    }
    return null;
  }, [location.pathname]);

  const [selectedModule, setSelectedModule] = useState(activeModule);

  useEffect(() => { if (open) setSelectedModule(activeModule); }, [open, activeModule]);

  const handleClose = useCallback(() => { onClose(); }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, handleClose]);

  const handleNav = (path) => { navigate(path); handleClose(); };
  const isChildActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const isModActive = (mod) => {
    if (mod.path) return location.pathname === mod.path;
    return mod.children?.some(c => isChildActive(c.path));
  };

  const handleModuleClick = (mod) => {
    if (mod.isLink) { handleNav(mod.path); return; }
    setSelectedModule(prev => prev?.key === mod.key ? null : mod);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            className="fixed inset-0 z-40 bg-black/10 backdrop-blur-[1px]"
            onClick={handleClose}
            data-testid="launcher-backdrop"
          />
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ type: 'spring', damping: 28, stiffness: 380 }}
            className="fixed top-[60px] left-4 z-50 w-[calc(100vw-2rem)] max-w-[1340px] bg-white rounded-2xl shadow-[0_20px_60px_-10px_rgba(0,0,0,0.18)] border border-gray-200/80 overflow-hidden"
            data-testid="launcher-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-3.5 border-b border-gray-100">
              <div>
                <h2 className="text-sm font-semibold text-gray-900" data-testid="launcher-title">All Modules</h2>
                <p className="text-[10px] text-gray-400">Navigate your ProcureX workspace</p>
              </div>
              <button onClick={handleClose} className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors" data-testid="launcher-close">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* 3-Column Section Grid (v3.0 — Change Order group eliminated) */}
            <div className="grid grid-cols-3 divide-x divide-gray-100">
              {SECTIONS.map((section, si) => (
                <div key={section.title} className="py-2.5 px-2">
                  <p className="px-2.5 py-1 text-[8px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-0.5">{section.title}</p>
                  <div className="space-y-px">
                    {section.modules.map((mod, mi) => {
                      const Icon = mod.icon;
                      const isSelected = selectedModule?.key === mod.key;
                      const active = isModActive(mod);
                      return (
                        <motion.div
                          key={mod.key}
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: (si * 3 + mi) * 0.012 }}
                          onClick={() => handleModuleClick(mod)}
                          className={`flex items-center gap-2 px-2.5 py-[5px] rounded-md cursor-pointer transition-all duration-150 ${
                            isSelected
                              ? 'bg-gray-100 ring-1 ring-gray-200'
                              : active
                                ? 'bg-[#1E4E8C]/[0.04] ring-1 ring-[#1E4E8C]/10'
                                : 'hover:bg-gray-50'
                          }`}
                          data-testid={`launcher-card-${mod.key}`}
                        >
                          <div
                            className="w-6 h-6 rounded-md flex items-center justify-center shrink-0"
                            style={{ background: `linear-gradient(135deg, ${mod.color}, ${mod.colorEnd})` }}
                          >
                            <Icon className="w-3 h-3 text-white" strokeWidth={2} />
                          </div>
                          <span className="text-[11px] font-medium text-gray-800 leading-tight truncate">{mod.label}</span>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom: Sub-module Drilldown */}
            <AnimatePresence mode="wait">
              {selectedModule?.children && (
                <motion.div
                  key={selectedModule.key}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                  className="overflow-hidden border-t border-gray-200 bg-gray-50/50"
                  data-testid="launcher-submodule-panel"
                >
                  <div className="px-6 py-4">
                    <div className="flex items-center gap-2.5 mb-3">
                      <div
                        className="w-7 h-7 rounded-lg flex items-center justify-center"
                        style={{ background: `linear-gradient(135deg, ${selectedModule.color}, ${selectedModule.colorEnd})` }}
                      >
                        <selectedModule.icon className="w-3.5 h-3.5 text-white" strokeWidth={2} />
                      </div>
                      <h3 className="text-sm font-semibold text-gray-900">{selectedModule.label}</h3>
                      <span className="text-[10px] text-gray-400 bg-gray-200/60 px-2 py-0.5 rounded-full">{selectedModule.children.length} sub-modules</span>
                    </div>

                    <div className="grid grid-cols-3 gap-x-6 gap-y-0.5 max-h-[200px] overflow-y-auto">
                      {selectedModule.children.map((child, i) => {
                        const active = isChildActive(child.path);
                        return (
                          <motion.div
                            key={child.path}
                            initial={{ opacity: 0, x: -4 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.015, duration: 0.12 }}
                            whileHover={{ x: 3 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleNav(child.path)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-all duration-150 ${
                              active
                                ? 'bg-[#1E4E8C]/5 border-l-2 border-[#1E4E8C] -ml-px'
                                : 'hover:bg-white border-l-2 border-transparent -ml-px'
                            }`}
                            data-testid={`launcher-sub-${child.path.split('/').pop()}`}
                          >
                            <span className={`text-xs leading-tight ${active ? 'text-[#1E4E8C] font-medium' : 'text-gray-600'}`}>
                              {child.label}
                            </span>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Settings footer — v3.0 spec §3.2 */}
            <div className="border-t border-gray-100 bg-gray-50 px-6 py-3 flex items-center gap-3 text-xs"
                  data-testid="launcher-settings-footer">
              <span className="text-gray-500 font-medium">⚙ Settings:</span>
              <button onClick={() => handleNav('/app/system-config')}
                        className="text-gray-700 hover:text-[#1E4E8C] hover:underline"
                        data-testid="launcher-settings-audit">
                Audit Log
              </button>
              <span className="text-gray-300">·</span>
              <button onClick={() => handleNav('/app/sourcing/award-doa')}
                        className="text-gray-700 hover:text-[#1E4E8C] hover:underline"
                        data-testid="launcher-settings-doa">
                Policies & DOA
              </button>
              <span className="text-gray-300">·</span>
              <button onClick={() => handleNav('/app/system-config')}
                        className="text-gray-700 hover:text-[#1E4E8C] hover:underline"
                        data-testid="launcher-settings-tenant">
                Tenant Config
              </button>
              <span className="text-gray-300">·</span>
              <button onClick={() => handleNav('/app/admin/import-master-data')}
                        className="text-gray-700 hover:text-[#1E4E8C] hover:underline"
                        data-testid="launcher-settings-import">
                Import Master Data
              </button>
              <span className="text-gray-300">·</span>
              <button onClick={() => handleNav('/app/admin')}
                        className="text-gray-700 hover:text-[#1E4E8C] hover:underline"
                        data-testid="launcher-settings-users">
                Users & Roles
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
